// Guardrails (02-DESIGN/07-guardrails/03-the-guardrails-service.md § the
// console; 01-the-console.md § the surfaces), under Administration: the rules
// as a list — the name, what it guards in words derived from the selector,
// on or off, the version — *Add a guardrail*, a form in the page's place,
// from a worked starting point or from nothing, the service's refusal shown
// beside the field it names, the escalation's details under *More options*. Beside them, the **required** rules (02-DESIGN/13-blueprints/03-required.md
// § required rules, § the console): evaluated alike, listed with the
// workspace's own and marked with who requires them — in a managed workspace
// its parent's, with no switch and no delete; in a solo one its admins', who
// write and remove them here. The list shows a rule's name, what it guards
// and its state; the expression leaves the list for the rule's own page,
// `guardrails/<rule>`, or a required rule's, `guardrails/required/<rule>`
// (RequiredGuardrailPage.tsx), where it is drawn whole (01-the-console.md
// § conventions: density and length; decision 0069).

import { watch } from "@impire-io/sdk";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { tenantPath } from "../../address/address.ts";
import {
  Button,
  Choices,
  describe,
  Empty,
  NotAllowed,
  PageHead,
  Pending,
  Pill,
  useLoad,
} from "../../ui/components.tsx";
import {
  requiredListSubject,
  requiredSubject,
  rulesListSubject,
  rulesWatchSubject,
  WATCH_HEADERS,
  ruleSubject,
  SERVICE,
  type Rule,
  type RuleEntry,
  type RulesReply,
} from "../../wire/guard.ts";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { Table, type Column, type MenuEntry } from "../../ui/table.tsx";
import { useSurface } from "../surface.tsx";
import { useManaged } from "../workspaces/managed.ts";
import { GuardRefusal, unserved } from "./Unserved.tsx";
import { draftOf, refusalFields, RuleForm, ruleOf, type Draft } from "./RuleForm.tsx";
import { BLANK, STARTERS } from "./starters.ts";
import { guards, ruleDoes, type RuleField } from "./words.ts";
import "../../ui/density.css";

/** Listed is one row: a workspace's own guardrail, or a required one with who requires it. */
interface Listed {
  entry: RuleEntry;
  by?: string;
}

/** subOf is the one fact under a guardrail's name: what it does. */
function subOf(x: Listed): string {
  return ruleDoes(x.entry.rule);
}

/**
 * useRuleChanges follows the rule set through its leased watch for as long
 * as the page is open, and counts the changes after the first snapshot — a
 * rule another admin wrote, or a required rule the steward put — so that the
 * lists read again and the page never shows a rule set that is gone. A fresh
 * snapshot after a gap counts as one change. A refusal of the watch leaves
 * the lists as their own reads show them.
 */
function useRuleChanges(): number {
  const s = useSurface();
  const bus = s.bus;
  const [n, setN] = useState(0);
  useEffect(() => {
    const ctl = new AbortController();
    void (async () => {
      let whole = false;
      try {
        for await (const ev of bus.follow<RuleEntry>(
          SERVICE,
          rulesWatchSubject(),
          WATCH_HEADERS,
          ctl.signal,
        )) {
          if (ev.kind === watch.Kind.change || (ev.kind === watch.Kind.complete && whole)) {
            setN((x) => x + 1);
          }
          if (ev.kind === watch.Kind.complete) {
            whole = true;
          }
        }
      } catch {
        // The lists' own reads show a refusal; the watch adds nothing to it.
      }
    })();
    return () => {
      ctl.abort();
    };
  }, [bus]);
  return n;
}

/**
 * ownGuardrailMenu is a workspace's own guardrail's row menu: *Switch off* or
 * *Switch on*, then *Delete*, asked first.
 */
export function ownGuardrailMenu(r: Rule, act: (a: "set" | "delete") => void): MenuEntry[] {
  return [
    {
      label: r.enabled ? "Switch off" : "Switch on",
      onSelect: () => {
        act("set");
      },
    },
    {
      label: "Delete",
      danger: true,
      confirm: {
        title: `Delete guardrail ${r.id}?`,
        consequence:
          "For good: every workload drops it within moments, and nothing it held waits any longer.",
      },
      onSelect: () => {
        act("delete");
      },
    },
  ];
}

export function GuardrailsPage() {
  const s = useSurface();
  const changed = useRuleChanges();
  const [list, reload] = useLoad(
    "guardrails",
    () => s.bus.call<RulesReply>(SERVICE, rulesListSubject(), undefined),
    changed,
    { keep: true },
  );
  const [required, reloadRequired] = useLoad(
    "required-guardrails",
    () => s.bus.call<RulesReply>(SERVICE, requiredListSubject(), undefined),
    changed,
    { keep: true },
  );
  const managed = useManaged();
  const form = useFormState<"own" | "required">();
  const [actErr, setActErr] = useState<unknown>(undefined);
  const [marked, setMarked] = useState<string | undefined>(undefined);
  const absent = list.state === "refused" && unserved(list.err);
  const create = (
    <Button
      kind="primary"
      onClick={() => {
        form.show("own");
      }}
      disabled={absent}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Add a guardrail
    </Button>
  );
  const requiredRules = required.state === "ready" ? required.value.rules.filter((e) => e.op === "put") : [];
  const removeRequired = (id: string) => {
    setActErr(undefined);
    s.bus.call<RuleEntry>(SERVICE, requiredSubject("delete", id), undefined).then(
      () => {
        reloadRequired();
      },
      (e: unknown) => {
        setActErr(e);
      },
    );
  };
  const rules = list.state === "ready" ? list.value.rules.filter((e) => e.op === "put") : [];
  const admins = `${s.name}'s admins`;
  const requiredBy = managed.managed ? managed.by : admins;
  const rows: Listed[] = [
    ...rules.map((entry) => ({ entry })),
    ...requiredRules.map((entry) => ({ entry, by: requiredBy })),
  ];
  const status = (x: Listed) => (x.by !== undefined ? "required" : x.entry.rule.enabled ? "on" : "off");
  const columns: Column<Listed>[] = [
    {
      key: "guardrail",
      header: "Guardrail",
      width: "minmax(0, 1.2fr)",
      value: (x) => x.entry.rule.id,
      text: (x) => `${x.entry.rule.id} ${subOf(x)}`,
      cell: (x) => (
        <span className="cell">
          <span className="cell-main">{x.entry.rule.id}</span>
          <span className="cell-sub">{subOf(x)}</span>
        </span>
      ),
    },
    {
      key: "guards",
      header: "Guards",
      width: "minmax(0, 1.8fr)",
      narrow: false,
      value: (x) => guards(x.entry.rule.selector),
      cell: (x) => <span className="small">{guards(x.entry.rule.selector)}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "minmax(100px, 0.8fr)",
      facet: "Status",
      value: status,
      text: (x) => (x.by !== undefined ? `required by ${x.by}` : status(x)),
      cell: (x) =>
        x.by !== undefined ? (
          <Pill strong>{x.by}</Pill>
        ) : x.entry.rule.enabled ? (
          <Pill tone="live">on</Pill>
        ) : (
          <Pill>off</Pill>
        ),
    },
    {
      key: "version",
      header: "Version",
      width: "80px",
      narrow: false,
      value: (x) => x.entry.revision,
      cell: (x) => <span className="small muted">{x.entry.revision}</span>,
    },
  ];
  // The workspace's own guardrail: switched off or on, and deleted, from its
  // row as on its page (01-the-console.md § conventions: a row's actions; remove).
  const ownAct = (x: Listed, act: "set" | "delete") => {
    setActErr(undefined);
    const r = x.entry.rule;
    s.bus
      .call<RuleEntry>(
        SERVICE,
        ruleSubject(act, r.id),
        act === "set" ? { ...r, enabled: !r.enabled } : undefined,
      )
      .then(
        () => {
          reload();
        },
        (e: unknown) => {
          setActErr(e);
        },
      );
  };
  const rowMenu = (x: Listed): MenuEntry[] =>
    x.by === undefined
      ? ownGuardrailMenu(x.entry.rule, (act) => {
          ownAct(x, act);
        })
      : requiredMenu(x);
  const requiredMenu = (x: Listed): MenuEntry[] =>
    x.by !== undefined && !managed.managed
      ? [
          {
            label: "Remove",
            danger: true,
            confirm: {
              title: `Remove required guardrail ${x.entry.rule.id}?`,
              consequence: "It stops applying at the next delivery to every workload.",
            },
            onSelect: () => {
              removeRequired(x.entry.rule.id);
            },
          },
        ]
      : [];
  const newRequired =
    required.state === "ready" && !managed.managed ? (
      <Button
        onClick={() => {
          form.show("required");
        }}
        disabled={absent}
      >
        Add a required guardrail
      </Button>
    ) : undefined;
  if (form.open !== undefined) {
    const req = form.open === "required";
    return (
      <NewGuardrail
        required={req}
        onClose={form.close}
        onCreated={(id) => {
          // Closing the form returns to the list, the new row marked.
          form.close();
          setMarked(`${req ? "required" : "own"}/${id}`);
          if (req) {
            reloadRequired();
          } else {
            reload();
          }
        }}
      />
    );
  }
  return (
    <>
      <PageHead
        title="Guardrails"
        line={`What ${s.name} lets through, refuses, holds for someone's confirmation, or hands to a transformer — on every call its workloads serve.`}
        action={
          <>
            {newRequired}
            {create}
          </>
        }
      />
      {list.state === "pending" && <Pending what="Reading the guardrails" />}
      {list.state === "refused" && (
        <GuardRefusal
          err={list.err}
          what="Guardrails"
          absent={`The guardrails service does not run in this install: every call ${s.name}'s workloads serve goes through, and a guardrail cannot be written until it does.`}
        />
      )}
      {actErr !== undefined && <NotAllowed err={actErr} />}
      {list.state === "ready" && rows.length === 0 && (
        <Empty action={create}>
          No guardrails yet: everything a workload serves goes through. Write one from a starting point.
        </Empty>
      )}
      {rows.length > 0 && (
        <Table
          label="Guardrails"
          rows={rows}
          rowKey={(x) => `${x.by === undefined ? "own" : "required"}/${x.entry.rule.id}`}
          sort={{ key: "guardrail", dir: "asc" }}
          mark={marked}
          to={(x) =>
            x.by === undefined
              ? tenantPath(s.tenant, "guardrails", x.entry.rule.id)
              : tenantPath(s.tenant, "guardrails", "required", x.entry.rule.id)
          }
          columns={columns}
          menu={rowMenu}
        />
      )}
      {requiredRules.length > 0 && (
        <p className="small faint">
          {managed.managed
            ? `${managed.by} requires the guardrails marked with its name: they apply beside ${s.name}'s own, and are changed only in its blueprint.`
            : `${s.name}'s admins require the guardrails marked as theirs of the workspace itself.`}
        </p>
      )}
    </>
  );
}

function NewGuardrail({
  onClose,
  onCreated,
  required = false,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
  required?: boolean;
}) {
  const s = useSurface();
  const [from, setFrom] = useState("blank");
  const [draft, setDraft] = useState<Draft>(draftOf(BLANK));
  const [refused, setRefused] = useState<Partial<Record<RuleField, string>>>({});
  const [err, setErr] = useState<unknown>(undefined);
  const [busy, setBusy] = useState(false);
  const start = (key: string) => {
    setFrom(key);
    const starter = STARTERS.find((st) => st.key === key);
    setDraft(draftOf(starter === undefined ? BLANK : starter.rule));
    setRefused({});
    setErr(undefined);
  };
  const ready =
    !busy && draft.id.trim() !== "" && draft.selector.trim() !== "" && draft.expression.trim() !== "";
  const submit = () => {
    if (!ready) {
      return;
    }
    const rule = ruleOf(draft);
    setBusy(true);
    setErr(undefined);
    setRefused({});
    let saved: Promise<RuleEntry>;
    try {
      saved = required
        ? s.bus.call<RuleEntry>(SERVICE, requiredSubject("set", rule.id), rule)
        : s.bus.call<RuleEntry>(SERVICE, ruleSubject("set", rule.id), rule);
    } catch (e) {
      setBusy(false);
      setRefused({ id: describe(e) });
      return;
    }
    saved.then(
      (entry) => {
        setBusy(false);
        onCreated(entry.rule.id);
      },
      (e: unknown) => {
        setBusy(false);
        const fields = refusalFields(e);
        setRefused(fields);
        if (Object.keys(fields).length === 0) {
          setErr(e);
        }
      },
    );
  };
  const chosen = STARTERS.find((st) => st.key === from);
  return (
    <FormPage
      title={required ? "Add a required guardrail" : "Add a guardrail"}
      line="A guardrail is checked on every call its pattern matches, in the workload that serves it, and concludes one of four things: let it through, refuse it, hold it until someone confirms, or hand it to a transformer that changes what passes."
      back="Guardrails"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={!ready}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="field">
        <span className="label">Start from</span>
        <Choices
          name="from"
          value={from}
          onChange={start}
          options={[
            ...STARTERS.map((st) => ({ value: st.key, label: st.title, hint: st.sentence })),
            { value: "blank", label: "Nothing", hint: "An empty guardrail that lets everything through." },
          ]}
        />
      </div>
      {chosen !== undefined && (
        <p className="small muted">{chosen.sentence} Read it below, change what you like, and save it.</p>
      )}
      {err !== undefined && <NotAllowed err={err} what="Save" />}
      <RuleForm draft={draft} onChange={setDraft} refused={refused} editing={false} onSubmit={submit} />
    </FormPage>
  );
}
